import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import "./searchableDropdownField.css";

const SearchableDropdown = ({
  options = [],
  label,
  id,
  selectedVal,
  handleChange,
  placeholder,
  onFocus,
  disabled,
  loading = false,
  onDropdownClick,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isBackspacing, setIsBackspacing] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [isAbove, setIsAbove] = useState(false);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery("");
        setIsBackspacing(false);
        setHighlightedIndex(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current && optionsRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const optionsHeight = optionsRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;

      setIsAbove(spaceBelow < optionsHeight && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    
    // Call onDropdownClick before toggling if dropdown is closed
    if (!isOpen && onDropdownClick) {
      onDropdownClick();
    }
    
    if (!isOpen && onFocus) {
      onFocus();
    }
    
    setIsOpen((prev) => !prev);
  };

  const selectOption = (option) => {
    if (option.disabled) return;
    handleChange(option[id]);
    setIsOpen(false);
    setQuery("");
    setIsBackspacing(false);
    setHighlightedIndex(null);
  };

  const getDisplayValue = () => {
    if (selectedVal) {
      const selectedOption = options.find(
        (option) => option[id] === selectedVal
      );
      return selectedOption ? selectedOption[label] : "";
    }
    return "";
  };

  const filterOptions = () => {
    return options.filter((option) =>
      option[label]?.toLowerCase().includes(query.toLowerCase())
    );
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
  };

  const handleKeyDown = (e) => {
    const optionsList = filterOptions();
    const numberOfOptions = optionsList.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (
        highlightedIndex === null ||
        highlightedIndex === numberOfOptions - 1
      ) {
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex(highlightedIndex + 1);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (highlightedIndex === null || highlightedIndex === 0) {
        setHighlightedIndex(numberOfOptions - 1);
      } else {
        setHighlightedIndex(highlightedIndex - 1);
      }
    } else if (e.key === "Enter" && highlightedIndex !== null) {
      const selectedOption = optionsList[highlightedIndex];
      if (!selectedOption.disabled) {
        selectOption(selectedOption);
      }
    }
  };

  return (
    <div
      className={`dropdown ${
        disabled ? "disabled-dropdown font-sm" : "font-sm"
      } ${isOpen ? "open font-sm" : "font-sm"}`}
      ref={inputRef}
    >
      <div className="control font-sm" onClick={toggleDropdown}>
        <div className="selected-value">
          <input
            type="text"
            value={getDisplayValue()}
            placeholder={placeholder || "Select"}
            readOnly
            disabled={disabled}
          />
        </div>
        <div className={`arrow ${isOpen ? "open" : ""}`}></div>
      </div>

      <div
        className={`options ${isOpen ? "open" : ""} ${isAbove ? "above" : ""}`}
        ref={optionsRef}
      >
        <div className="search-container font-sm">
          <Search className="search-icon font-sm" size={16} />
          <input
            ref={searchInputRef}
            type="text"
            className="search-input font-sm"
            placeholder="Find fls"
            value={query}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyDown}
          />
        </div>
        {loading ? (
          <div className="dropdown-loading-item">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#ad0f14]"></div>
            <span className="text-sm text-gray-500 ml-2">Loading roles...</span>
          </div>
        ) : filterOptions().length > 0 ? (
          filterOptions().map((option, index) => (
            <div
              onClick={() => selectOption(option)}
              className={`option ${
                option[id] === selectedVal ? "selected" : ""
              } ${highlightedIndex === index ? "highlighted" : ""} ${
                option.disabled ? "disabled-option" : ""
              }`}
              key={`${id}-${index}`}
            >
              {option[label]}
            </div>
          ))
        ) : (
          <div className="no-options">No Roles Found</div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;